import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { Box, Fade, Typography } from "@mui/material";
import { useSearchParams } from "react-router";
import { ActiveFiltersLoadingSkeleton } from "./ActiveFiltersLoadingSkeleton";
import { ActiveFiltersStack } from "./ActiveFiltersStack";
import {
	formatArrayValue,
	formatDateValue,
	formatSortingValue,
} from "./filterUtils";
import { formatFieldName } from "./stringUtils";

interface ActiveFiltersDisplayProps {
	orderCount: number;
	isLoading: boolean;
}

interface Filter {
	key: string;
	label: string;
	value: string;
	displayValue: string;
}

export const ActiveFiltersDisplay = ({
	orderCount,
	isLoading,
}: ActiveFiltersDisplayProps) => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Get all filter parameters (excluding pagination)
	const getActiveFilters = (): Filter[] => {
		const filters: Filter[] = [];
		const excludeParams = [
			"pageIndex",
			"pageLimit",
			"marketId",
			"unassignedPartner",
			"unassignedUser",
			"notReviewed",
		];

		for (const [key, value] of searchParams.entries()) {
			if (!excludeParams.includes(key) && value) {
				// Format the parameter name to be more readable
				const label = formatFieldName(key);

				// Handle special cases for better display
				let displayValue = value;

				// Handle boolean values
				if (
					key === "unassignedPartner" ||
					key === "unassignedUser" ||
					key === "notReviewed" ||
					key === "hasExamination"
				) {
					displayValue = value === "true" ? "Yes" : "No";
				}
				// Handle sorting
				else if (key === "sorting") {
					displayValue = formatSortingValue(value);
				}
				// Handle date fields
				else if (
					key.toLowerCase().includes("date") ||
					key.toLowerCase().includes("created") ||
					key.toLowerCase().includes("updated")
				) {
					displayValue = formatDateValue(value);
				}
				// Handle array-like values
				else if (
					value.includes(",") ||
					(value.startsWith("[") && value.endsWith("]"))
				) {
					displayValue = formatArrayValue(value);
				}

				filters.push({
					key,
					label,
					value,
					displayValue,
				});
			}
		}

		return filters;
	};

	const removeFilter = (key: string, value: string) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.delete(key, value);
		// Reset to first page when filters change
		newParams.set("pageIndex", "0");
		setSearchParams(newParams);
	};

	const clearAllFilters = () => {
		const newParams = new URLSearchParams();
		// Keep only pagination and sorting params
		const keepParams = ["pageLimit", "sorting"];
		keepParams.forEach((param) => {
			const value = searchParams.get(param);
			if (value) {
				newParams.set(param, value);
			}
		});
		newParams.set("pageIndex", "0");
		setSearchParams(newParams);
	};

	const activeFilters = getActiveFilters();

	if (activeFilters.length === 0) {
		return null;
	}

	// Show loading skeleton while data is being fetched
	if (isLoading) {
		return <ActiveFiltersLoadingSkeleton />;
	}

	return (
		<Fade in={activeFilters.length > 0} timeout={400}>
			<Box mb={1}>
				<BaseCard
					sxCardContent={{
						py: 1,
						px: 0,
						"&:last-child": {
							pb: 1,
						},
					}}
				>
					<Box
						display="flex"
						flexDirection={{ xs: "column", sm: "row" }}
						alignItems={{ xs: "stretch", sm: "center" }}
						justifyContent="space-between"
						marginLeft={2}
						marginRight={2}
						gap={{ xs: 1, sm: 2 }}
					>
						<Box display="flex" flexDirection="column" gap={0.5} flexShrink={0}>
							<Box display="flex" alignItems="center" gap={1.5}>
								<Typography
									variant="subtitle2"
									sx={{
										textTransform: "uppercase",
										letterSpacing: 0.5,
										fontWeight: 600,
										fontSize: { xs: "0.75rem", sm: "0.875rem" },
									}}
								>
									Active Filters{" · "} {activeFilters.length}
								</Typography>
							</Box>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
							>
								{orderCount.toLocaleString()}{" "}
								{orderCount === 1 ? "order" : "orders"}
							</Typography>
						</Box>
						<Box flex="1" minWidth={0} mt={{ xs: 1, sm: 0 }}>
							<ActiveFiltersStack
								activeFilters={activeFilters}
								onRemoveFilter={removeFilter}
								onClearAll={clearAllFilters}
							/>
						</Box>
					</Box>
				</BaseCard>
			</Box>
		</Fade>
	);
};
